import jwtDecode from 'jwt-decode';
import PropTypes from 'prop-types';
import secureLocalStorage from 'react-secure-storage';
import React, {
  useMemo,
  useState,
  useEffect,
  useContext,
  useCallback,
  createContext,
} from 'react';

import authService from 'src/services/auth.service';
import {
  getUserById,
  getUserAvatarUrl,
  getUserPermissions,
} from 'src/services/user.service';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(secureLocalStorage.getItem('user') || null);
  const [user, setUser] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);

  const logout = useCallback(async () => {
    try {
      const storedToken = secureLocalStorage.getItem('user');
      if (storedToken) {
        const decoded = jwtDecode(storedToken);
        await authService.logout(decoded.id);
      }
    } catch (e) {
      console.error(e);
    } finally {
      secureLocalStorage.clear();
      setToken(null);
      setUser(null);
    }
  }, []);

  const fetchUser = useCallback(async (authToken) => {
    if (!authToken) return;
    try {
      const decoded = jwtDecode(authToken);

      const [apiUser, permsRes] = await Promise.all([
        getUserById(decoded.id),
        getUserPermissions(decoded.id),
        getUserPermissions(decoded.id),
      ]);

      let departmentIdsArray = [];

      if (
        apiUser.departmentIds &&
        Array.isArray(apiUser.departmentIds) &&
        apiUser.departmentIds.length > 0
      ) {
        departmentIdsArray = apiUser.departmentIds;
      } else if (
        decoded.uniqueDepartmentIds &&
        Array.isArray(decoded.uniqueDepartmentIds) &&
        decoded.uniqueDepartmentIds.length > 0
      ) {
        departmentIdsArray = decoded.uniqueDepartmentIds;
      } else if (apiUser.departmentId) {
        departmentIdsArray = [apiUser.departmentId];
      }

      setUser({
        ...apiUser,
        role: decoded.role,
        permissions: permsRes.permissions || [],
        departmentIds: departmentIdsArray,
        firstLogin: decoded.firstLogin,
        avatarUrl: apiUser.avatarVersion
          ? getUserAvatarUrl(apiUser.id, apiUser.avatarVersion)
          : null,
      });
    } catch (e) {
      console.error('[Auth] Initialization Error:', e);
      secureLocalStorage.clear();
      setToken(null);
      setUser(null);
    } finally {
      setIsInitializing(false);
    }
  }, []);

  useEffect(() => {
    if (token) fetchUser(token);
    else setIsInitializing(false);
  }, [token, fetchUser]);

  const login = useCallback(
    async (accessToken) => {
      const res = await authService.login(accessToken);
      secureLocalStorage.setItem('user', res.authenticationToken);
      setToken(res.authenticationToken);
      await fetchUser(res.authenticationToken);
      return res;
    },
    [fetchUser]
  );

  const updatePermissions = useCallback((newPermissions) => {
    setUser((prevUser) => {
      if (!prevUser) return null;
      return {
        ...prevUser,
        permissions: newPermissions || [],
      };
    });
  }, []);

  const contextValue = useMemo(
    () => ({
      id: user?.id || null,
      role: user?.role || null,
      departmentIds: user?.departmentIds || [],
      permissions: user?.permissions || [],
      updatePermissions,
      isAuthenticated: !!token && !!user,
      isInitializing,
      username: user?.username || '',
      givenName: user?.givenName || '',
      familyName: user?.familyName || '',
      user,
      token,
      login,
      logout,
      fetchUser,
    }),
    [token, user, isInitializing, login, logout, fetchUser, updatePermissions]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {!isInitializing && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
AuthProvider.propTypes = { children: PropTypes.node };
